import { BaseRepository } from "./base-repository";
import { sql } from "@/domains/database/utils";
import { first } from "lodash-es";

type SizeResult = { total_size_bytes: string };

export class StatsRepository extends BaseRepository {
  async getTotalSizeBytes(): Promise<number> {
    const sizeResult = await this.query<SizeResult>({
      sql: sql`
        SELECT
          COALESCE(SUM(pg_total_relation_size(quote_ident(table_name))), 0) AS total_size_bytes
        FROM information_schema.tables
        WHERE table_schema = 'public'
      `,
    });

    const total = parseInt(first(sizeResult.rows)?.total_size_bytes || "0", 10);
    return Number.isFinite(total) ? total : 0;
  }

  async getPublicTableNames(): Promise<string[]> {
    const result = await this.query<{ table_name: string }>({
      sql: sql`
        SELECT table_name
        FROM information_schema.tables
        WHERE table_schema = 'public'
          AND table_type = 'BASE TABLE'
        ORDER BY table_name
      `,
    });

    return result.rows.map(r => r.table_name);
  }

  async getRowCountsByTable(): Promise<Array<{ table: string; rowCount: number }>> {
    const tableNames = await this.getPublicTableNames();

    const countPromises = tableNames.map(async table => {
      // We double-quote the table name to avoid issues with casing/special characters.
      const countSql = sql`SELECT COUNT(*)::BIGINT AS row_count FROM "${table}"`;
      const res = await this.query<{ row_count: string }>({ sql: countSql });
      const rowCount = parseInt(first(res.rows)?.row_count || "0", 10);
      return { table, rowCount: Number.isFinite(rowCount) ? rowCount : 0 };
    });

    const settled = await Promise.allSettled(countPromises);

    const results = settled
      .filter(
        (r): r is PromiseFulfilledResult<{ table: string; rowCount: number }> =>
          r.status === "fulfilled"
      )
      .map(r => r.value);

    results.sort((a, b) => a.table.localeCompare(b.table));
    return results;
  }
}
